import _ from "lodash";
import React, { createRef } from "react";
import { getAssessmentToolItem } from "Utils/AssessmentToolHelpers";

// Components
import UIBaseComponent from "UIComponents/UIBaseComponent";
import StudentMeasureAssessing from "./components/StudentMeasureAssessing";
import CustomDropdown from "../CustomDropdown";
import CustomMediaCard from "../CustomMediaCard";
import CollapsibleAssessmentTools from "../CollapsibleAssessmentTools";

// Module
import {
  updateAssessmentInRedux,
  updateAssessment,
} from "Assessments/modules/AssessmentModule";
import { createAssessmentTool } from "AssessmentToolSetup/modules/AssessmentToolSetupModule";
import { getCategoriesForGradeBoundaryTool } from "ClassRoom/modules/utils";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import {
  getCurrentCurriculumProgramType,
  getIsStaff,
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import {
  updateActiveQuestion,
  updateAIAssessmentTools,
  updateWorksheetSidebar,
} from "Worksheet/modules/WorksheetModules";
import { getSelectedLearningCourseId } from "LearningCourse/modules/LearningCourseUtils";
import { getIsWorksheetAIEnabled } from "Worksheet/modules/WorksheetFeatureFlags";

// External
import { connect } from "react-redux";
import { IconButton } from "@toddle-edu/ds-web";
import { DeleteOutlined, StarsTwoFilled } from "@toddle-edu/ds-icons";

// Constants
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import {
  SUBJECT_TYPE_GENERIC,
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT,
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  DP_ASSESSMENT_RUBRIC,
  STANDARD_BASED_RUBRIC,
  WORKSHEET_AI_ASSESSMENT_TOOLS,
  COMMENT,
  SCORE,
  TODDLE_LIBRARY,
  ASSESSMENT_TYPE_WORKSHEET,
} from "Constants/stringConstants";

// Scss
import classes from "./MeasureAssessingWrapper.scss";

// tools which needs to be rendered through a modal

const MODAL_TOOLS = [
  MYP_OBJECTIVE_RUBRIC,
  MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC,
  DP_ASSESSMENT_RUBRIC,
  STANDARD_BASED_RUBRIC,
];

const styles = {
  subtitleStyle: {
    maxWidth: "280px",
  },
  starsTwoFilledIconStyle: {
    color: "var(--border-selected)",
  },
};

const handleDropdownMenuChange = async props => {
  const {
    e,
    params,
    assessmentToolData,
    updateAssessmentInRedux,
    updateAssessmentToolData,
    parentId,
    parentType,
    assessmentId,
    createAssessmentTool,
    assessmentTools,
    subjectType,
    createMode = "MODAL",
    portalType,
    curriculumProgramId,
    gradeScales,
  } = props;

  const key = _.get(e, "key");
  const type = _.get(params, "measureAssessing");

  const toggleAssessmentToolModal = () => {
    updateAssessmentToolData({
      showAssessmentToolModal: !assessmentToolData?.showAssessmentToolModal,
    });
  };

  // in DP assessment rubric there is only option to select templates
  // for other tools like rubric, checklist, single point rubric we show separate option to select templates
  const showTemplates =
    key === "DP_ASSESSMENT_RUBRIC" || _.startsWith(key, "use_from_template");

  if (showTemplates) {
    updateAssessmentInRedux({
      assessmentToolId: "",
      assessmentToolType: type,
    });

    updateAssessmentToolData({
      assessmentToolModalMode: "use_from_template",
    });
  } else {
    const tool = _.find(ASSESSMENT_TOOLS, tool => tool?.type == type);

    if (tool) {
      switch (type) {
        case COMMENT:
        case SCORE: {
          return await createAssessmentTool({
            type,
            parentId,
            parentType,
            assessmentId,
            portalType,
            curriculumProgramId,
          });
        }
        case "IB_GRADE_SCALE": {
          if (_.startsWith(key, ASSESSMENT_GRADE_BOUNDARY)) {
            return await createAssessmentTool({
              type: ASSESSMENT_GRADE_BOUNDARY,
              parentId,
              parentType,
              assessmentId,
              extraParams: {
                label: type,
                associatedEntites: [
                  {
                    id: parentId,
                    type: parentType,
                  },
                ],
                categories: getCategoriesForGradeBoundaryTool({
                  subjectType,
                }),
              },
              portalType,
              curriculumProgramId,
            });
          } else {
            updateAssessmentInRedux({
              assessmentToolId: "",
              assessmentToolType: key,
            });
          }

          break;
        }
        default: {
          if (createMode === "CREATE") {
            const standardBasedRubric = getAssessmentToolItem(
              assessmentTools,
              STANDARD_BASED_RUBRIC
            );

            const gradeScaleId = _.get(standardBasedRubric, "gradeScale.id");

            const gradeScaleEntityMapId = _.get(
              _.find(gradeScales, { gradeScaleId }) || _.first(gradeScales),
              "key"
            );

            const extraParams = { gradeScaleEntityMapId };

            return await createAssessmentTool({
              type,
              parentId,
              parentType,
              assessmentId,
              portalType,
              isGlobal: false,
              curriculumProgramId,
              extraParams,
            });
          } else {
            updateAssessmentInRedux({
              assessmentToolId: "",
              assessmentToolType: type,
            });
            updateAssessmentToolData({
              assessmentToolModalMode: "create_new",
            });
          }
        }
      }
    }
  }
  toggleAssessmentToolModal();
};

class MeasureAssessing extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      assessmentToolData: {
        showAssessmentToolModal: false,
        assessmentToolModalMode: "",
        assessmentToolMode: "edit",
      },
    };

    this.toolsRef = createRef();
    this.toolsRef.current = {};
  }

  isValid = () => {
    const { assessmentTools } = this.props;

    const activeAssessmentTools = _.map(assessmentTools?.edges, ({ node }) =>
      _.get(node, "assessmentToolType")
    );

    const totalErrors = _.reduce(
      this.toolsRef.current,
      (acc, ref, toolType) => {
        const isActiveToolOrTaskWeight =
          toolType === "TASK_WEIGHT" ||
          _.includes(activeAssessmentTools, toolType);

        if (isActiveToolOrTaskWeight) {
          acc += ref?.isValid?.() ?? 0;
        }

        return acc;
      },
      0
    );

    return totalErrors;
  };

  // set initial value of max score in redux
  componentDidMount = () => {
    const { mode, assessmentTools = [], updateAssessmentInRedux } = this.props;

    const { maxScore } = getScoreDetailsMemoize({
      assessmentTools,
    });

    if (mode === "edit" && !_.isEmpty(maxScore)) {
      updateAssessmentInRedux({ maxScore });
    }
  };

  getCreateMode = params => {
    const { parentType } = this.props;
    const type = _.get(params, "measureAssessing");

    switch (parentType) {
      case ASSESSMENT: {
        if (_.includes(MODAL_TOOLS, type)) return "MODAL";
        return "CREATE";
      }
      default:
        return "MODAL";
    }
  };

  // IMPORTANT: do not change the function name as it being being accessed via ref in PrerequisiteSelection.js
  onChangeDropDownMenuOption = async (e, setIsToolLoading) => {
    const {
      fieldKey,
      assessmentFields,
      options,
      updateInputField,
      currentSubject,
      updateAIAssessmentTools,
      updateWorksheetSidebar,
    } = this.props;

    const { key, keyPath } = e;

    // to open AIAssessmentTools (assessment tools AI assistant) in case of overall assessment tool of the worksheet
    if (key === "overallAssessmentTool") {
      updateAIAssessmentTools({ isOpen: true });
      // closing sidebar on smaller screens for better visibility when AIAssessmentTools is opened
      if (window.innerWidth < 1192) {
        updateWorksheetSidebar({ isOpen: false });
      }
      return;
    }

    const params = {
      [`${fieldKey}`]: !_.isEmpty(keyPath) ? _.last(keyPath) : key,
    };

    const hasMeasureAssessing = !_.isEmpty(
      _.find(assessmentFields, field => {
        return _.get(field, "uid") === "measureAssessing";
      })
    );

    if (hasMeasureAssessing && _.last(keyPath) !== "IB_GRADE_SCALE") {
      updateInputField?.(params, { nodes: options }, false);
    }

    if (setIsToolLoading) {
      setIsToolLoading(prev => [...prev, _.last(keyPath)]);
    }

    await handleDropdownMenuChange({
      ...this.props,
      e,
      params,
      createMode: this.getCreateMode(params),
      assessmentToolData: this.state.assessmentToolData,
      updateAssessmentToolData: this.updateAssessmentToolData,
      subjectType: _.get(currentSubject, "0.type", SUBJECT_TYPE_GENERIC),
    });

    if (setIsToolLoading) {
      setIsToolLoading(prev => _.without(prev, _.last(keyPath)));
    }
  };

  // IMPORTANT: do not change the function name as it being being accessed via ref in PrerequisiteSelection.js
  updateAssessmentToolData = data => {
    this.setState(prevState => {
      return {
        ...prevState,
        assessmentToolData: {
          ..._.get(prevState, "assessmentToolData", {}),
          ...data,
        },
      };
    });
  };

  setToolsRef = (key, ref) => {
    this.toolsRef.current[key] = ref;
  };

  debouncedUpdateAssessment = _.debounce(this.props?.updateAssessment, 500);

  shouldShowEmpty = () => false;

  renderTools = () => {
    const {
      mode,
      curriculumProgramType,
      assessmentTools = [],
      sourceFrom,
      parentType,
      testId,
    } = this.props;

    const mediaCard = (
      <CustomMediaCard
        {..._.omit(this.props, ["customRef"])}
        assessmentToolData={this.state.assessmentToolData}
        updateAssessmentToolData={this.updateAssessmentToolData}
        assessmentTools={assessmentTools?.edges}
        curriculumProgramType={curriculumProgramType}
        debouncedUpdateAssessment={this.debouncedUpdateAssessment}
      />
    );

    if (mode === "view" && sourceFrom !== "UNIT_PLAN") {
      return mediaCard;
    } else {
      switch (parentType) {
        case ASSESSMENT: {
          return (
            <CollapsibleAssessmentTools
              {..._.omit(this.props, ["customRef"])}
              assessmentToolData={this.state.assessmentToolData}
              updateAssessmentToolData={this.updateAssessmentToolData}
              assessmentTools={assessmentTools?.edges}
              curriculumProgramType={curriculumProgramType}
              debouncedUpdateAssessment={this.debouncedUpdateAssessment}
              testId={testId}
              setToolsRef={this.setToolsRef}
            />
          );
        }
        default:
          return mediaCard;
      }
    }
  };

  // * In Worksheet single page flow, open AIAssessmentTools (assessment tools AI assistant)
  handleAIAssessmentTools = () => {
    const {
      updateAIAssessmentTools,
      updateWorksheetSidebar,
      updateActiveQuestion,
    } = this.props;
    updateAIAssessmentTools({ isOpen: true });
    updateActiveQuestion(WORKSHEET_AI_ASSESSMENT_TOOLS);
    // closing sidebar on smaller screens for better visibility when AIAssessmentTools is opened
    if (window.innerWidth < 1192) {
      updateWorksheetSidebar({ isOpen: false });
    }
  };

  // * In Worksheet single page flow, remove all the assessment tools except score
  removeAndCloseAssessmentTool = () => {
    const { assessmentTools, removeAllAssessmentTools } = this.props;

    // * Mapping id and type of assessment tools to be removed
    // * We are removing all the assessment tools except score
    const assessmentToolsToRemove = _.map(
      _.filter(
        _.get(assessmentTools, "edges", []),
        tool => !_.isEqual(tool?.node?.assessmentToolType, "SCORE")
      ),
      tool => {
        const type = _.get(tool, "node.assessmentToolType", "");
        const id = _.get(tool, "node.assessmentToolItem.id", "");
        return { id, type };
      }
    );
    removeAllAssessmentTools(assessmentToolsToRemove);
  };

  renderDropdown = (params = {}) => {
    const {
      mode = "view",
      curriculumProgramType,
      assessmentTools,
      testId,
      assessmentFields,
      isFromToddleLibrary,
      assessmentType,
    } = this.props;

    const assessmentToolEdges =
      _.get(params, "assessmentTools.edges", null) ||
      _.get(assessmentTools, "edges", []);

    const isWorksheetDynamicField = _.get(
      params,
      "isWorksheetDynamicField",
      false
    );
    const isWorksheet = assessmentType === ASSESSMENT_TYPE_WORKSHEET;
    const isWorksheetAIEnabled = getIsWorksheetAIEnabled();
    const showAIAssistant =
      isWorksheet && !isFromToddleLibrary && isWorksheetAIEnabled;

    return mode !== "view" ? (
      <div className={classes.assessmentToolControllerContainer}>
        {showAIAssistant && (
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="fill"
            icon={<StarsTwoFilled variant={"link"} />}
            size="small"
            shouldStopPropagation={true}
            testId={`${testId}-assessment-tools-ai-assistant`}
            onClick={this.handleAIAssessmentTools}
            className={classes.aiAssessmentToolBtn}
          />
        )}

        <CustomDropdown
          {...this.props}
          isWorksheetDynamicField={isWorksheetDynamicField}
          assessmentTools={assessmentToolEdges}
          onChangeDropDownMenuOption={this.onChangeDropDownMenuOption}
          curriculumProgramType={curriculumProgramType}
          testId={`${testId}-addAssessment`}
          assessmentFields={params?.assessmentFields || assessmentFields}
          gradeScales={params?.gradeScales}
        />

        {isWorksheet && (
          <IconButton
            dsVersion="2.0"
            type="plain"
            variant="neutral"
            size="small"
            icon={<DeleteOutlined />}
            onClick={this.removeAndCloseAssessmentTool}
            testId={`${testId}-assessment-tools-remove-all`}
          />
        )}
      </div>
    ) : null;
  };

  renderView = () => {
    const { measureAssessingContainerStyle = {} } = this.props;

    return (
      <div className={classes.container} style={measureAssessingContainerStyle}>
        {this.renderTools()}
      </div>
    );
  };

  renderEdit = () => {
    const {
      showDropdown = false,
      measureAssessingContainerStyle = {},
    } = this.props;

    return (
      <div className={classes.container} style={measureAssessingContainerStyle}>
        {this.renderTools()}
        {/* Empty state when no assessment tools are available in the overall assessment tools section of the worksheet */}
        {showDropdown ? this.renderDropdown() : null}
      </div>
    );
  };
}

const MeasureAssessingWrapper = props => {
  const { isStaff, sourceFrom } = props;

  // render StudentMeasureAssessing only for student/family end from class stream
  return isStaff || sourceFrom !== "STUDENT_CLASS_STREAM" ? (
    <MeasureAssessing {...props} />
  ) : (
    <StudentMeasureAssessing {...props} />
  );
};

const mapStateToProps = (state, ownProps) => {
  const { learningCourseId: ownPropsLearningCourseId } = ownProps;

  const assessmentId = _.get(
    state,
    "assessment.assessmentData.assessmentId",
    ""
  );

  const type =
    getCurrentCurriculumProgramType(state) || _.get(ownProps, "curriculumType");
  const curriculumProgramId =
    getSelectedCurriculumId(state) || _.get(ownProps, "curriculumProgram.id");

  const paramsCourseId = _.get(ownProps, "params.course_id");
  let selectedCourseId = _.get(state, "teacher.selected_class.selected_course");

  selectedCourseId = !_.isEmpty(selectedCourseId)
    ? selectedCourseId
    : ownProps?.selectedCourseId;

  const courseId = paramsCourseId ? paramsCourseId : selectedCourseId;

  const learningCourseId =
    _.get(state, "planner.unitPlanData.learningCourseId", null) ||
    getSelectedLearningCourseId(state) ||
    ownPropsLearningCourseId;

  const academicYearId = getSelectedAcademicYearId(state);

  const isFromToddleLibrary =
    _.get(state, "login.activeTab") === TODDLE_LIBRARY;

  return {
    assessmentId,
    curriculumProgramType: type,
    courseId,
    isStaff: getIsStaff(state),
    curriculumProgramId,
    learningCourseId,
    academicYearId,
    isFromToddleLibrary,
  };
};

const mapActionCreators = {
  updateAssessmentInRedux,
  createAssessmentTool,
  updateAssessment,
  updateAIAssessmentTools,
  updateWorksheetSidebar,
  updateActiveQuestion,
};

export default connect(
  mapStateToProps,
  mapActionCreators
)(MeasureAssessingWrapper);
